import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type * as Paths from "./paths";

interface OperationMethods {
  "getConfigNumbers"(
    parameters?: Parameters<Paths.GetConfigNumbers.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetConfigNumbers.Responses.$200>;

  "createConfigNumber"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateConfigNumber.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateConfigNumber.Responses.$200>;

  "getConfigNumber"(
    parameters?: Parameters<Paths.GetConfigNumber.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetConfigNumber.Responses.$200>;

  "updateConfigNumber"(
    parameters?: Parameters<Paths.UpdateConfigNumber.PathParameters> | null,
    data?: Paths.UpdateConfigNumber.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateConfigNumber.Responses.$200>;

  "deleteConfigNumber"(
    parameters?: Parameters<Paths.DeleteConfigNumber.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteConfigNumber.Responses.$204>;
}

export const getConfigNumberOperations = (client: AxiosInstance) => {
  const getConfigNumbers: OperationMethods["getConfigNumbers"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/config-number";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createConfigNumber: OperationMethods["createConfigNumber"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/config-number";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getConfigNumber: OperationMethods["getConfigNumber"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/config-number/{configNumber}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateConfigNumber: OperationMethods["updateConfigNumber"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/config-number/{configNumber}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteConfigNumber: OperationMethods["deleteConfigNumber"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/config-number/{configNumber}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  return {
    getConfigNumbers: getConfigNumbers,
    createConfigNumber: createConfigNumber,
    getConfigNumber: getConfigNumber,
    updateConfigNumber: updateConfigNumber,
    deleteConfigNumber: deleteConfigNumber,
  };
};
